<template>
  <div class="invoice-details-grid">
    <table class="invoice-details-grid__table">
      <thead class="invoice-details-grid__thead">
        <tr class="invoice-details-grid__header-row">
          <th class="invoice-details-grid__header-cell">{{ getContentItem("orders") }}</th>
          <th class="invoice-details-grid__header-cell" />
          <th class="invoice-details-grid__header-cell">{{ getContentItem("Line") }}</th>
          <th class="invoice-details-grid__header-cell">{{ getContentItem("PartNumber") }}</th>
          <th class="invoice-details-grid__header-cell">{{ getContentItem("description") }}</th>
          <template v-if="details.isNotRapidPartsInvoice">
            <th class="invoice-details-grid__header-cell">{{ getContentItem("poNumber") }}</th>
            <th class="invoice-details-grid__header-cell">{{ getContentItem("htcCode") }}</th>
          </template>
          <th class="invoice-details-grid__header-cell">{{ getContentItem("container") }}</th>
          <th class="invoice-details-grid__header-cell">{{ getContentItem("shipped") }}</th>
          <th v-if="isSapDealerWithSoldTo" class="invoice-details-grid__header-cell">
            {{ getContentItem("dealer") }}
          </th>
          <th class="invoice-details-grid__header-cell">
            {{ getContentItem("trackingNumber") }}
          </th>
          <th v-if="showLineItemMasterTrackingInfo" class="invoice-details-grid__header-cell">
            {{ getContentItem("masterTrackingNumber") }}
          </th>
          <template v-if="details.showPrices">
            <th v-if="details.SubTotalEmergencySurcharges" class="invoice-details-grid__header-cell">
              Emergency Fee
            </th>
            <th class="invoice-details-grid__header-cell">{{ getContentItem("price") }}</th>
            <th class="invoice-details-grid__header-cell">{{ getContentItem("ExtCoreCosts") }}</th>
            <th class="invoice-details-grid__header-cell">{{ getContentItem("ExtPrice") }}</th>
          </template>
        </tr>
      </thead>
      <tbody class="invoice-details-grid__tbody">
        <template v-for="(item, index) in lineItems">
          <tr :key="index" class="invoice-details-grid__row">
            <td 
              v-if="item.rowSpan" 
              :rowspan="item.rowSpan + GetRowSpanForComments()"
              class="invoice-details-grid__cell invoice-details-grid__cell--order"
            >
              <related-order-link v-if="GetRelatedOrder(item.SalesOrderNumber) !== undefined" :item="GetRelatedOrder(item.SalesOrderNumber)" />
              <span v-else>
                {{ item.SalesOrderNumber }}
              </span>
            </td>
            <template v-if="item.ItemNumber === 'M'">
              <td class="invoice-details-grid__cell" />
              <td class="invoice-details-grid__cell invoice-details-grid__cell--right invoice-details-grid__cell--muted">
                {{ item.SalesOrderLineNumber }}
              </td>
              <td class="invoice-details-grid__cell invoice-details-grid__cell--muted">
                <em>Comment</em>
              </td>
              <td colspan="100%" class="invoice-details-grid__cell invoice-details-grid__cell--muted">
                {{ item.ItemDescription }}
              </td>
            </template>
            <template v-else>
              <td class="invoice-details-grid__cell">
                <input type="checkbox" :value="item" v-model="selectedLineItems" />
              </td>
              <td class="invoice-details-grid__cell">{{ item.SalesOrderLineNumber }}</td>
              <td class="invoice-details-grid__cell">
                <part-details-link :oem-part-code="item.ItemNumber" />
              </td>
              <td class="invoice-details-grid__cell">{{ item.ItemDescription }}</td>
              <template v-if="details.isNotRapidPartsInvoice">
                <td class="invoice-details-grid__cell">{{ item.PurchaseOrderNumber }}</td>                
                <td class="invoice-details-grid__cell">{{ item.HarmonizedTarriffCode }}</td>
              </template>
              <td class="invoice-details-grid__cell">{{ item.ContainerNumber }}</td>
              <td class="invoice-details-grid__cell">{{ item.QuantityShipped }}</td>
              <td v-if="isSapDealerWithSoldTo" class="invoice-details-grid__cell">
                {{ item.SoldTo }}
              </td>
              <td class="invoice-details-grid__cell">
                <template v-if="item.CarrierTrackingUrl">
                  <a :href="item.CarrierTrackingUrl">{{ item.CarrierTrackingNumber }}</a>
                </template>
                <template v-else>
                  {{ item.CarrierTrackingNumber }}
                </template>
              </td>
              <td v-if="showLineItemMasterTrackingInfo" class="invoice-details-grid__cell">
                <template v-if="item.MasterCarrierTrackingUrl">
                  <a :href="item.MasterCarrierTrackingUrl">{{ item.MasterCarrierTrackingNumber }}</a>
                </template>
                <template v-else>
                  {{ item.MasterCarrierTrackingNumber }}
                </template>
              </td>
              <template v-if="details.showPrices">
                <td v-if="details.SubTotalEmergencySurcharges" class="invoice-details-grid__cell">
                  <currency-text :amount="item.EmergencySurcharge" :currency-code="details.CurrencyCode" />
                </td>
                <td class="invoice-details-grid__cell">
                  <currency-text :amount="item.UnitPrice" :currency-code="details.CurrencyCode" />
                </td>
                <td class="invoice-details-grid__cell">
                  <currency-text :amount="item.CoreCharge" :currency-code="details.CurrencyCode" />
                </td>
                <td class="invoice-details-grid__cell">
                  <currency-text
                    :amount="item.ExtendedPrice"
                    :currency-code="details.CurrencyCode"
                  />
                </td>
              </template>
            </template>
          </tr>
          <template v-for="(comment, commentIndex) in item.Comments">
            <tr :key="commentIndex" class="invoice-details-grid__row invoice-details-grid__row--comment" />
            <tr :key="commentIndex" class="invoice-details-grid__row invoice-details-grid__row--comment">
              <td class="invoice-details-grid__cell" />
              <td class="invoice-details-grid__cell">
                {{ item.SalesOrderLineNumber }}
              </td>
              <td class="invoice-details-grid__cell">
                {{ getContentItem("Comment") }}
              </td>
              <td :colspan="4" class="invoice-details-grid__cell">
                {{ comment }}
              </td>
              <td class="invoice-details-grid__cell" />
            </tr>
          </template>
        </template>
      </tbody>
      <tfoot v-if="details.showPrices" class="invoice-details-grid__tfoot">
        <tr v-if="showStartClaimButtonLabel" class="invoice-details-grid__row invoice-details-grid__row--sub-total">
          <td colspan="6" class="invoice-details-grid__cell invoice-details-grid__cell--claim-label">
            <label>
              <strong>
                Cannot start claim, invoice is older than {{ this.claimAllowedDays }} days.
              </strong>
            </label>
          </td>
        </tr>
        <tr v-if="showIscalaReturnsDisabledLabel" class="invoice-details-grid__row invoice-details-grid__row--sub-total">
          <td colspan="6" class="invoice-details-grid__cell invoice-details-grid__cell--claim-label">
            <label>
              <strong>
                {{ getContentItem("IscalaInvoice")}}
              </strong>
            </label>
          </td>
        </tr>
        <tr class="invoice-details-grid__row invoice-details-grid__row--sub-total">
          <td class="invoice-details-grid__cell">
            <DnFunctionButton
              id="createClaim"
              :callback-fn="createClaim"
              :text="getContentItem('startClaim')"
              :size="largeSize"
              extra-classes="invoice-details-grid__claim-button"
              :loading="isLoadingClaimData"
              :disabled="disableCreateClaimButton"
            />
          </td>
          <td colspan="100%" class="invoice-details-grid__cell">
            <label for="subtotal" class="invoice-details-grid__inline-label">{{ getContentItem("subtotal") }}:</label>
            <currency-text
              id="subtotal"
              :amount="details.SubTotalParts"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.SubTotalEmergencySurcharges" class="invoice-details-grid__row invoice-details-grid__row--sub-fee">
          <td colspan="100%" class="invoice-details-grid__cell">
            <label
              for="emergency-surcharges"
              class="invoice-details-grid__inline-label"
            >{{ getContentItem("emergencySurcharge") }}:</label>
            <currency-text
              id="emergency-surcharges"
              :amount="details.SubTotalEmergencySurcharges"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.SubTotalCores" class="invoice-details-grid__row invoice-details-grid__row--sub-fee">
          <td colspan="100%" class="invoice-details-grid__cell">
            <label for="subtotal-cores" class="invoice-details-grid__inline-label">{{ getContentItem("coreCost") }}:</label>
            <currency-text
              id="subtotal-cores"
              :amount="details.SubTotalCores"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.SubTotalDiscount" class="invoice-details-grid__row invoice-details-grid__row--sub-fee">
          <td colspan="100%" class="invoice-details-grid__cell">
            <label for="restock-fee" class="invoice-details-grid__inline-label">{{ getContentItem("restockFee") }}:</label>
            <currency-text
              id="restock-fee"
              :amount="details.SubTotalDiscount"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr class="invoice-details-grid__row invoice-details-grid__row--sub-fee">
          <td colspan="100%" class="invoice-details-grid__cell">
            <label for="freight" class="invoice-details-grid__inline-label">{{ getContentItem("freight") }}:</label>
            <currency-text
              id="freight"
              :amount="details.SubTotalFreight"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.SubtotalVat" class="invoice-details-grid__row invoice-details-grid__row--sub-fee">
          <td colspan="100%" class="invoice-details-grid__cell">
            <label for="gst" class="invoice-details-grid__inline-label">{{ getContentItem("gst") }}:</label>
            <currency-text
              id="gst"
              :amount="details.SubtotalVat"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.OtherFees" class="invoice-details-grid__row invoice-details-grid__row--sub-fee">
          <td colspan="100%" class="invoice-details-grid__cell">
            <label for="other-fees" class="invoice-details-grid__inline-label">{{ getContentItem("otherFees") }}:</label>
            <currency-text
              id="other-fees"
              :amount="details.OtherFees"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.DealerClaimAmount" class="invoice-details-grid__row invoice-details-grid__row--sub-fee">
          <td colspan="100%" class="invoice-details-grid__cell">
            <label for="claimAmount" class="invoice-details-grid__inline-label">{{ getContentItem("ClaimAmount") }}:</label>
            <currency-text
              id="claimAmount"
              :amount="details.DealerClaimAmount"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.DealerServiceFee" class="invoice-details-grid__row invoice-details-grid__row--sub-fee">
          <td colspan="100%" class="invoice-details-grid__cell">
            <label for="dealerServiceFee" class="invoice-details-grid__inline-label">{{ getContentItem("DealerServiceFee") }}:</label>
            (<currency-text
              id="dealerServiceFee"
              :amount="details.DealerServiceFee"
              :currency-code="details.CurrencyCode"
            />)
          </td>
        </tr>
        <tr v-if="details.TotalInvoice && details.showTotal" class="invoice-details-grid__row invoice-details-grid__row--total">
          <td colspan="100%" class="invoice-details-grid__cell">
            <label for="total" class="invoice-details-grid__inline-label">{{ getContentItem("total") }}:</label>
            <currency-text
              id="total"
              :amount="details.TotalInvoice"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
        <tr v-if="details.SubTotalDiscount" class="invoice-details-grid__row invoice-details-grid__row--discount">
          <td colspan="100%" class="invoice-details-grid__cell">
            <label for="discount" class="invoice-details-grid__inline-label">{{ getContentItem("cashDiscountAvailable") }}:</label>
            <currency-text
              id="discount"
              :amount="details.SubTotalDiscount"
              :currency-code="details.CurrencyCode"
            />
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
  import { mapGetters } from "vuex";

  // components
  import { CurrencyText, PartDetailsLink } from "@/components/Elements";
  import RelatedOrderLink from "@/components/Invoices/RelatedOrderLink"
  import DnFunctionButton from "@/components/Elements/DnFunctionButton"
  
  // mixins
  import { sizeHelper } from "@/components/mixins/sizeHelper"

  // services 
  import { ClaimsService } from "@/services/ClaimsService"

  export default {
    name: "InvoiceDetailsGrid",
    components: {
      CurrencyText,
      PartDetailsLink,
      RelatedOrderLink,
      DnFunctionButton
    },
    mixins: [ sizeHelper ],
    data: () => ({
      selectedLineItems: [],
      isLoadingClaimData: false,
      claimAllowedDays: null
    }),
    created() {
          return ClaimsService.GetClaimAllowedDays(this.McfCostCenterId).then(days => {
            this.claimAllowedDays = days;
          });
    },
    computed: {
      ...mapGetters([
        "getContentItem", 
        "user", 
        "activeAccountId",
        "McfCostCenterId",
      ]),

      lineItems() {
        const list = this.details.LineItems || [];

        return list.map((item, i) => {
          return {
            ...item,
            rowSpan:
              i === 0 || item.SalesOrderNumber !== list[i - 1].SalesOrderNumber
                ? list.filter(x => x.SalesOrderNumber === item.SalesOrderNumber)
                  .length
                : null
          };
        });
      },

      isSuperReturnAdmin() { 
        return this.user.UserPermissions.includes("IgnoreReturnValidation");
      },

      isPastClaimAllowedDays() {

        const dayLength = 24 * 60 * 60 * 1000;
        const invoiceDate = new Date(this.details.InvoiceDate);
        const today = new Date();
        const difference = Math.round(Math.abs((today - invoiceDate) / dayLength));

        return difference > this.claimAllowedDays;
      },

      isSapDealerWithSoldTo() {
        return (
          this.details.InvoiceSource !== "Rapidsparts" &&
          this.lineItems.some(x => x.SoldTo)
        );
      },

      disableCreateClaimButton() {
        const noPartSelected = this.selectedLineItems.length < 1;
        const pastMaxClaimDays = this.isPastClaimAllowedDays;
        const canReturnPastMaxDays = this.isSuperReturnAdmin;
        const isIscalaInvoice = this.details.SourceCode === "ISC";

        if (noPartSelected) {
          return true;
        }

        if (isIscalaInvoice) {
          return true;
        }
             
        if (canReturnPastMaxDays) {
          return false;
        } else if (pastMaxClaimDays) {
          return true;
        } else {
          return false;
        }
        // fallback to not allow
        return true;
      },

      showStartClaimButtonLabel() {
        return this.isPastClaimAllowedDays && !this.isSuperReturnAdmin;
      },

      showIscalaReturnsDisabledLabel(){
        return this.details.SourceCode === "ISC";
      },

      showLineItemTrackingInfo() {
        return this.lineItems.some(x => x.CarrierTrackingNumber);
      },

      showLineItemMasterTrackingInfo() {
        return this.lineItems.some(x => x.MasterCarrierTrackingNumber);
      }
    },

    methods: {
      GetRelatedOrder(orderNumber) {
        let self = this;
        return this.relatedOrders.find(function(element) {

          if(element.OrderNumber === orderNumber) {
            if(self.$route.query.invoiceSource == "Rapidparts") {
                element.OrderSource = "Rpi";
              }
          }

          return element.OrderNumber === orderNumber
        })
        return undefined
      },
      
      async getPastClaimAllowedDays() {
        //load claim allowed days
        var claimAllowedDays = await ClaimsService.GetClaimAllowedDays(this.McfCostCenterId);
        return claimAllowedDays;
      },
      
      createClaim(){
        this.isLoadingClaimData = true
        //Had an issue with 'this' going out of scope in the below arrow function
        let self = this;
        ClaimsService.LoadClaimInvoiceItems(self.activeAccountId, self.user.UserId, self.$route.query.invoiceId, self.$route.query.invoiceSource).then(allInvoiceItems => {
          let selectedLineIds = self.selectedLineItems.map(x => x.Id);
          let selectedInvoiceItems = allInvoiceItems.data.filter(x => selectedLineIds.includes(x.InvoiceLineId));

          let invoiceId = self.$route.query.invoiceId;
          let invoiceNumber = self.details.InvoiceNumber
          let invoiceSource = self.details.InvoiceSource
          let invoiceDate = self.details.InvoiceDate
          let salesOrg = self.details.SalesOrganization

          let relatedOrder = self.details.RelatedOrders[0]
          let orderNumber = relatedOrder.OrderNumber
          let dealerCode = relatedOrder.DealerCode

          let claimData = {
            CustomerReferenceNumber: `${dealerCode} - ${invoiceNumber}`,
            Source: String,
            InvoiceSource: invoiceSource,
            InvoiceDate: invoiceDate,
            InvoiceNumber: invoiceNumber,
            InvoiceFreight: null,
            DealerCode: dealerCode,
            OrderNumber: orderNumber,
            Items: selectedInvoiceItems,
            ContactName: String(),
            ContactPhone: String(),
            ContactPhoneExtension: String(),
            ContactEmail: String(),
            ContactFax: String(),
            SalesOrganization: salesOrg,
            ClaimType: "PartsIssue",
            FreightExpectedCredit: 0.00,
            FreightComment: "",
            ClaimImagesBase64: [],
            ClaimImagesFilenames: [],
            SourceInvoiceNumber: self.details.SourceInvoiceNumber,
            SourceCode: self.details.SourceCode
          }

          let invoiceData = {
            invoiceId: invoiceId,
            invoiceSource: invoiceSource
          }

          self.$store.commit("SET_RECENT_COMPONENT", {
            name: "InvoiceCreditDetails",
            data: {
              claimData: claimData,
              invoiceData: invoiceData,
              currentTabId: "claimInfoTab"
            },
          });

          this.isLoadingClaimData = false

          this.$router.push({
            name: "CreateClaim",
            query: { fromInvoice: true}
          })
        })
      },
      GetRowSpanForComments(){
        var count = 0;
        for(var i = 0; i < this.details.LineItems.length; i++){
          count += this.details.LineItems[i].Comments.length;
        }
        return count * 2;
      }
    },

    props: {
      details: Object,
      relatedOrders: Array
    }
  };
</script>

<style lang="scss">
.invoice-details-grid {
  width: 100%;
  overflow-x: auto;
  font-family: "Segoe UI", "Roboto", "Arial", sans-serif;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  padding: 16px;
  margin: 1rem 0rem;
}

.invoice-details-grid__table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.invoice-details-grid__header-cell,
.invoice-details-grid__cell {
  border-bottom: 1px solid #e3eaf3;
  padding: 10px 12px;
  text-align: left;
  transition: background 0.2s;
}

.invoice-details-grid__header-cell {
  background: #f4f6fb;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e3eaf3;
  user-select: none;
  cursor: pointer;
  transition: background 0.2s;
}

.invoice-details-grid__header-cell:hover {
  background: #eaf1fb;
}

.invoice-details-grid__cell--right {
  text-align: right;
}

.invoice-details-grid__cell--muted {
  color: #888;
  font-style: italic;
}

.invoice-details-grid__row:nth-child(even) {
  background: #f8fafc;
}

.invoice-details-grid__row:hover {
  background: #eaf1fb;
}

.invoice-details-grid__row--sub-total,
.invoice-details-grid__row--sub-fee,
.invoice-details-grid__row--total,
.invoice-details-grid__row--discount {
  background: #f4f6fb;
  font-weight: 600;
}

.invoice-details-grid__row--comment {
  background: #fffbe6;
}

.invoice-details-grid__inline-label {
  margin-right: 8px;
  font-weight: 500;
}

.invoice-details-grid__claim-button {
  width: 110px;
}

.invoice-details-grid__cell--claim-label {
  text-align: left;
  color: red;
  padding: 5px 0 0 5px;
}

// Empty state styling
.invoice-details-grid__empty-row {
  text-align: center;
  color: #888;
  padding: 32px;
}

// Pagination styles (if needed in future)
.invoice-details-grid__pagination {
  margin-top: 18px;
  text-align: right;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

.invoice-details-grid__pagination button {
  background: #1976d2;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 16px;
  cursor: pointer;
  transition: background 0.2s;
}

.invoice-details-grid__pagination button:disabled {
  background: #b0c4de;
  cursor: not-allowed;
}

.invoice-details-grid__pagination span {
  color: #333;
  margin: 0 8px;
}

.invoice-details-grid__page-selector {
  border: 1px solid #b0c4de;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 15px;
  margin: 0 4px;
  cursor: pointer;
}
</style>